import { PredicatedViewObserver } from '@/components/util/PredicatedViewObserver';
import { isProductOutOfStock, PRODUCT_PURCHASE_ISSUE_TEXT } from '@/pages/umeds/order-medication/util';
import type { Price } from '@montugroup/circuit-shared/types/product';
import type { IntervalDetails } from '@montugroup/circuit-shared/types/refill';
import { Box, Button, Chip, Typography } from '@montugroup/design-system';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import type { Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { svgIconClasses } from '@mui/material/SvgIcon';
import type { ReactNode } from 'react';
import { DispenseIntervalAlert } from './DispenseIntervalAlert';
import { formatPrice } from './util';

export interface UnavailableProductData {
  id: number;
  name: string;
  price: Price;
  is_out_of_stock: boolean;
  reasoning_toggle: boolean;
  is_generative_scripting: boolean;
  brand: null | string;
  intervalDetails?: IntervalDetails;
  token?: string;
  prescriptionCode?: string;
}

export interface UnavailableProductIssue {
  title: string;
  info: (string | ReactNode)[];
  isHighSeverity?: boolean;
  requiresInfoOutline?: boolean;
}

const UNKNOWN_SUPPLIER = 'Unknown Supplier';

const isOOSorDiscontinued = (product: UnavailableProductData) =>
  product.is_out_of_stock || product.reasoning_toggle || product.is_generative_scripting;

export const UnavailableProductCardRoot = styled(Box)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.action.disabledBackground,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignContent: 'flex-start',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(2),
}));

export const Instructions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'start',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.warning.light,
  [`& .${svgIconClasses.root}`]: {
    color: theme.palette.warning.main,
  },
  '&.severity-high': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
}));

export interface UnavailableProductRefillCardProps {
  product: UnavailableProductData;
  issue: UnavailableProductIssue;
  handleCheckProductStatus: (() => void) | null;
  showRequestReplacementOption?: boolean;
  showDispenseInterval?: boolean;
  reorderDate?: string;
}

export const ProductStatusChip = styled(Chip)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.warning.main,
  fontWeight: 600,
  backgroundColor: theme.palette.warning.light,
  border: `1px solid ${theme.palette.warning.main}`,
  '&.severity-high': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
  },
  '&.dispense-interval': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
  },
}));

export const DullText = styled(Typography)(({ theme }) => ({
  color: theme.palette.action.disabled,
  fontWeight: 600,
}));

const ActionText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  padding: theme.spacing(2),
  color: theme.palette.primary.main,
  borderColor: theme.palette.action.disabledBackground,
}));

export function UnavailableProductRefillCard(props: UnavailableProductRefillCardProps) {
  const { product, issue, handleCheckProductStatus, showRequestReplacementOption, showDispenseInterval, reorderDate } =
    props;
  const severityClass = issue.isHighSeverity ? 'severity-high' : '';
  const supplierName = product.brand ?? UNKNOWN_SUPPLIER;
  const hasInfoText = issue.info && issue.info.length > 0;

  let chipLabel = issue.title;

  if (issue.title === 'Interval time lock' && showDispenseInterval) {
    chipLabel = PRODUCT_PURCHASE_ISSUE_TEXT.dispenseInterval.title;
  }

  const isDispenseInterval = chipLabel === PRODUCT_PURCHASE_ISSUE_TEXT.dispenseInterval.title && showDispenseInterval;
  const chipClass = isDispenseInterval ? 'dispense-interval' : severityClass;

  // Only track when the user sees an OOS Product card
  const viewObserverProps = {
    enabled: isProductOutOfStock(product),
    id: `oos-productcard-${product.id}`,
  };

  return (
    <PredicatedViewObserver {...viewObserverProps}>
      <UnavailableProductCardRoot aria-label={`Card for Unavailable Product: ${product.name}`}>
        <Box>
          <ProductStatusChip className={chipClass} variant="outlined" label={chipLabel} />
        </Box>
        <DullText variant="subtitle2" sx={{ fontWeight: 600 }} aria-hidden>
          {product.name}
        </DullText>

        {Boolean(product?.prescriptionCode) && (
          <DullText variant="body2" aria-label="Script ID">
            Script ID <strong>{product.prescriptionCode}</strong>
          </DullText>
        )}

        <DullText variant="body2">
          {product?.token ? (
            <span aria-label="Token">Script token {product.token}</span>
          ) : (
            <>
              <span aria-label="Supplier">{supplierName}</span>
              <span aria-hidden>&nbsp;|&nbsp;</span>
              <span>
                RRP <strong>{formatPrice(product.price)}</strong>
              </span>
            </>
          )}
        </DullText>
        {isDispenseInterval ? (
          <DispenseIntervalAlert reorderDate={reorderDate} intervalStatus="LOCKED" />
        ) : (
          hasInfoText && (
            <Instructions className={severityClass}>
              {!issue.isHighSeverity && <InfoOutlined />}
              {issue.requiresInfoOutline && <InfoOutlined />}
              <Box sx={{ display: 'block' }}>
                {issue.info.map((info, index) => {
                  return (
                    <Typography
                      key={`refill-card-info-${index}`}
                      sx={{
                        '&:not(:first-of-type)': {
                          mt: 4,
                        },
                      }}
                    >
                      {info}
                    </Typography>
                  );
                })}
              </Box>
            </Instructions>
          )
        )}
        {Boolean(handleCheckProductStatus) &&
          Boolean(showRequestReplacementOption) &&
          isOOSorDiscontinued(product) &&
          handleCheckProductStatus && (
            <Button fullWidth variant="outlined" onClick={handleCheckProductStatus}>
              <ActionText variant="body1">Check product status</ActionText>
            </Button>
          )}
      </UnavailableProductCardRoot>
    </PredicatedViewObserver>
  );
}

export default UnavailableProductRefillCard;
